import { Injectable } from "@angular/core";
import { EvtAlertActions, QuoteActions } from "@core/store/actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import {
  catchError,
  concatMap,
  filter,
  from,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { EvtAlertAdapter } from './evt-alert.adapter';
import { ProductsService } from '@core/services/products.service';
import { addError } from "@entities/error/error.action";
import { EvtAlertService } from "./evt-alert.service";
import { rateQuoteFail } from "@entities/quote/quote.action";
import { updateEvtAgentReply, updateEvtAgentReplyFail, updateEvtAgentReplySuccess } from "./evt-alert.action";
import { ErrorSanitizerService } from "@core/services/error-sanitizer.service";
import { clearProductError, updateProductStatus } from "@entities/product/product.actions";

@Injectable({
  providedIn: 'root',
})
export class EvtAlertEffects {
  constructor(
    private actions$: Actions,
    private evtAlertAdapter: EvtAlertAdapter,
    private evtAlertService: EvtAlertService,
    private productsService: ProductsService,
    private errorSanitizerService: ErrorSanitizerService
  ) {}

  fetchEvtAlertsAfterCertainRatingFailures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuoteActions.rateQuoteFail),
      filter((action) => this.shouldCheckAlertsForRatingError(action)),
      mergeMap((action) =>
        this.productsService.getQuoteIdForProduct(action.productType).pipe(
          take(1),
          map((quoteId) => ({ action, quoteId }))
        )
      ),
      mergeMap(({ action, quoteId }) =>
        this.evtAlertAdapter.fetchEvtAlerts(action.productType, quoteId).pipe(
          concatMap((response) => {
            const fromActions: Action[] = [
              EvtAlertActions.replaceEvtAlertsForQuote({
                productType: action.productType,
                quoteId,
                response,
              }),
            ];
            const replyRequiredAlertMessage = response
              .find((alert) => this.evtAlertService.alertRequiresReply(alert.message))
              ?.message
            // Per TIB-1550:
            // If we find an alert that requires a reply, generate a unique error and skip the rating retry
            // Otherwise, retry bind rating as usual
            if (
                this.evtAlertService.alertRequiresReply((action.error as any)?.developerMessage) &&
                replyRequiredAlertMessage
              ) {
              fromActions.push(
                addError({
                  payload: {
                    productType: action.productType,
                    displayMessage: this.formatReplyWarningMessage(replyRequiredAlertMessage),
                    developerMessage: replyRequiredAlertMessage,
                    severity: 'warning',
                    sourceActionType: rateQuoteFail.type
                  },
                })
              )
            } else {
              fromActions.push(
                QuoteActions.rateQuote({
                  productType: action.productType,
                  ratingType: 'bind',
                  retryReason: 'EVTAlerts',
                })
              );
            }
            return from(fromActions);
          }),
          catchError((error) => {
            return from([]);
          })
        )
      )
    )
  );

  updateEvtAgentReply$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateEvtAgentReply),
      mergeMap((action) => 
        this.evtAlertAdapter.updateEvtAgentReply(action.agentReply, action.productType, action.quoteId).pipe(
          concatMap((response) => [
              clearProductError({ payload: action.productType }),
              updateEvtAgentReplySuccess({ agentReply: response, productType: action.productType, quoteId: action.quoteId }),
            ]
          ),
          catchError((error) => [updateEvtAgentReplyFail({ error: this.errorSanitizerService.sanitizeError(error) })])
        )
      )
    )
  );

  dropEvtAlertsOnRatingRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuoteActions.rateQuote),
      filter((action) => action.retryReason !== 'EVTAlerts'),
      map((action) =>
        EvtAlertActions.dropEvtAlertsForProduct({
          productType: action.productType,
        })
      )
    )
  );

  private shouldCheckAlertsForRatingError(action: any): boolean {
    if (action.ratingType !== 'bind') {
      return false;
    }
    // If this rating call was an EVT-related retry, do not call.
    if (action.retryReason === 'EVTAlerts') {
      return false;
    }
    // This "EVTAlerts" will only be present on the first RC2 failure per quote.
    // That's by design tho I really can't say why.
    if (action?.error?.developerMessage?.includes?.('EVTAlerts') ||
        this.evtAlertService.alertRequiresReply(action?.error?.developerMessage)) {
      return true;
    }
    return false;
  }

  // Applies to AM001 warning display message only
  // removes error code and square brackets
  private formatReplyWarningMessage(displayMessage: string): string {
    return displayMessage.replace('AM001', '').replace(/[\[\]]/g, '').trim();
  }
}
